import { setPincodeList } from "../../store/slices/common/integratedGoogleSuggestion";
import { fetchContentFromCMSOnClient } from "../common";
import { AVAILABLE_PINCODES, fetchCityPincodeBasedOnLocation, fetchPincodeJson, fetchPlaceDetails } from "./constants";
import { InterceptorClient } from "../../interceptor/interceptorClient";
import { GOOGLE_GEOCODE_API_KEY, GOOGLE_GEOCODE_API_URL } from "../../constants/keys/locationLock";

const devBaseUrl = process.env.NEXT_PUBLIC_BASE_URL;

/**
 * Fetches the raw pincode list for a two-digit prefix from the serviceability API.
 * Each row looks like { "242001": "Shahjahanpur, Uttar Pradesh", lat: "27.94", long: "79.95" }.
 *
 * @param {string} pincode first two digits of pincode.
 * @returns {Promise<Array>} raw pincode rows.
 */
export async function fetchPincodeList(pincode) {
  const response = await InterceptorClient.getGatewayInstance().get(fetchPincodeJson(pincode), false);
  return Array.isArray(response) ? response : [];
}

/**
 * Converts raw pincode rows into unique { pincode, location, lat, long } entries.
 *
 * @param {Array} response raw pincode rows.
 * @returns {Array} unique pincode entries.
 */
export const sanitizePincodeList = (response = []) => {
  return response.reduce((unique, row) => {
    const pincode = Object.keys(row || {}).find((key) => /^\d+$/.test(key));
    if (pincode && !unique.some((item) => item.pincode === pincode)) {
      unique.push({ pincode, location: row[pincode], lat: row.lat, long: row.long });
    }
    return unique;
  }, []);
};

/**
 * This function fetches pincode list by using the first two digit of pincode
 *
 * @param {*} pincode  it refers to first two digit of pincode.
 * @param {Function} dispatch dispatcher
 * @param {Array} availablePincodes get all available pincode
 * @returns {Array} rel alternate data on success and empty on exception.
 */
export async function getPincodes(pincode, dispatch, availablePincodes) {
  if (availablePincodes?.length && availablePincodes.indexOf(pincode) > -1) {
    try {
      const response = await fetchPincodeList(pincode);
      dispatch(setPincodeList(sanitizePincodeList(response)));
      return response;
    } catch (ex) {
      //   error.config.url = `assets/js/pincodes/${pincode}.json`;
      //   error.response.status = `An exception occurred while fetching pincode JSON => ${ex}`;
      //   errorLogger(error, SHOW_REQUESTS_LOGS);
      return [];
    }
  } else {
    return [];
  }
}

/**
 * generate search  term suggestions
 *
 * @returns {Array} return the available pincodes.
 */
export async function getAvailablePincodes() {
  try {
    const response = await fetchContentFromCMSOnClient(AVAILABLE_PINCODES);
    return response;
  } catch (ex) {
    throw new Error({ ex, isSuccess: true });
  }
}

/**
 * Get near by pin code
 *
 * @param {Array} pinCodeList pincode list
 * @param {string} localPinCode pincode
 * @returns {Array} get near pin code list
 */
export const getPincodeSetter = (pinCodeList, localPinCode) => {
  return pinCodeList.filter((item) => Number(localPinCode) === Number(item.pincode.slice(0, localPinCode.length)));
};

/**
 * This function gets pin code for a provided location
 *
 * @param {number} latitude this is the latitude of the location selected through google map
 * @param {number} longitude this is the longitude of the location selected through google map
 * @returns {void} returns nothing
 */
export async function getCityPincodeBasedOnLocation(latitude, longitude) {
  try {
    return await InterceptorClient.getFNTSInstance(devBaseUrl).get(
      fetchCityPincodeBasedOnLocation(latitude, longitude),
      false,
    );
  } catch (e) {
    // error.config.url = "d/control/getPinCodeByPostalAddress";
    // error.response.status = `An exception occurred while calling fetching city name => ${e}`;
    // errorLogger(error, SHOW_REQUESTS_LOGS);
    return {};
  }
}

// Result types preferred for the address (same as the app); used when no exact (rooftop) result is close by.
const PREFERRED_GEOCODE_TYPES = ["premise", "subpremise", "sublocality", "neighborhood"];
// Named places (building / establishment / point of interest) read as the exact address of the spot.
const PLACE_GEOCODE_TYPES = ["premise", "establishment", "point_of_interest"];
// A named place within this distance (metres) of the pin is its address, even if Google gives its centre point.
const NAMED_PLACE_MAX_DISTANCE_METERS = 100;
// Otherwise the nearest rooftop (exact building point) address within this distance (metres).
const ROOFTOP_MAX_DISTANCE_METERS = 50;
// Leading Google Plus Code, e.g. "J6Q5+PGP".
const PLUS_CODE_PREFIX_REGEX = /^[A-Z0-9]{4}\+[A-Z0-9]{2,}/;

/**
 * Approximate distance (metres) between a geocoder result and the requested point.
 * @param {Object} result - Geocoding API result.
 * @param {number} latitude
 * @param {number} longitude
 * @returns {number}
 */
const getResultDistance = (result, latitude, longitude) => {
  const lat = Number(result?.geometry?.location?.lat);
  const lng = Number(result?.geometry?.location?.lng);
  if (!lat || !lng || !latitude || !longitude) return Infinity;
  const metersPerDegree = 111000;
  return Math.hypot(
    (lat - latitude) * metersPerDegree,
    (lng - longitude) * metersPerDegree * Math.cos((latitude * Math.PI) / 180),
  );
};

/**
 * Most accurate address result for the point, skipping plus-code results:
 * 1. the nearest named place (premise / establishment / point of interest) within 100 m, rooftop ones first;
 * 2. else the nearest rooftop (exact building point) address within 50 m;
 * 3. else the app's preferred types; else the first non plus-code result.
 * @param {Array} results - Geocoding API results.
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Object} chosen result.
 */
const getMostAccurateGeocodeResult = (results, latitude, longitude, { preferRooftop = true } = {}) => {
  const candidates = results
    .filter(
      (result) =>
        !(result?.types || []).includes("plus_code") &&
        !PLUS_CODE_PREFIX_REGEX.test(String(result?.formatted_address || "").trim()),
    )
    .map((result) => ({
      result,
      distance: getResultDistance(result, latitude, longitude),
      isRooftop: result?.geometry?.location_type === "ROOFTOP",
      isNamedPlace: (result?.types || []).some((type) => PLACE_GEOCODE_TYPES.includes(type)),
    }));
  const namedPlace = candidates
    .filter(({ isNamedPlace, distance }) => isNamedPlace && distance <= NAMED_PLACE_MAX_DISTANCE_METERS)
    .sort((a, b) => Number(b.isRooftop) - Number(a.isRooftop) || a.distance - b.distance)[0];
  if (namedPlace) return namedPlace.result;
  // Map drag skips the rooftop step (a rooftop hit is often a bare house number); current location keeps it.
  const rooftop = !preferRooftop
    ? null
    : candidates
        .filter(({ isRooftop, distance }) => isRooftop && distance <= ROOFTOP_MAX_DISTANCE_METERS)
        .sort((a, b) => a.distance - b.distance)[0];
  if (rooftop) return rooftop.result;
  return (
    candidates.find(({ result }) => (result?.types || []).some((type) => PREFERRED_GEOCODE_TYPES.includes(type)))
      ?.result ||
    candidates[0]?.result ||
    results[0]
  );
};

// Bare number ("33", "5646") or short house code ("F - 65", "A-12", "12B") as the first address part.
const BARE_NUMBER_REGEX = /^\d+$/;
const HOUSE_CODE_REGEX = /^[A-Za-z]{0,2}\s*[-/]?\s*\d+\s*[A-Za-z]?$/;

/**
 * Readable title from address parts: a bare number is dropped for the next two parts; a house code keeps the
 * next part (its street); anything else is the first part.
 * @param {string[]} parts - Address parts (country / plus code already removed).
 * @returns {string}
 */
const getGeocodeLabel = (parts) => {
  const [first = "", ...rest] = parts;
  if (BARE_NUMBER_REGEX.test(first) && rest.length) return rest.slice(0, 2).join(", ");
  if (HOUSE_CODE_REGEX.test(first) && rest.length) return [first, rest[0]].join(", ");
  return first;
};

/**
 * Picks the address from a Geocoding API response: the most accurate result for the point (see
 * getMostAccurateGeocodeResult), formatted address without the trailing country and a leading plus code (as the app).
 * @param {Array} results - Geocoding API results.
 * @param {number} latitude
 * @param {number} longitude
 * @param {Object} [options]
 * @param {boolean} [options.preferRooftop=true] - false for map drag (skip the rooftop step).
 * @returns {Object|null} { primary, label, secondary, full, pincode, city, latitude, longitude, result } or null.
 */
export const parseGeocodeResults = (results, latitude, longitude, options = {}) => {
  if (!Array.isArray(results) || !results.length) return null;
  const preferred = getMostAccurateGeocodeResult(results, latitude, longitude, options);
  const parts = String(preferred?.formatted_address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  // Drops the trailing country and a leading plus-code, neither of which reads as an address.
  if (parts[parts.length - 1] === "India") parts.pop();
  if (parts[0] && PLUS_CODE_PREFIX_REGEX.test(parts[0])) parts.shift();
  // Drops a leading floor / flat (subpremise, e.g. "4th Floor"): it belongs in "Address details", not the locality.
  const subpremiseNames = (preferred?.address_components || [])
    .filter((component) => (component?.types || []).includes("subpremise"))
    .map((component) =>
      String(component?.long_name || "")
        .trim()
        .toLowerCase(),
    );
  while (parts.length > 1 && subpremiseNames.includes(parts[0].toLowerCase())) parts.shift();
  let pincode = "";
  let city = "";
  (preferred?.address_components || []).forEach((component) => {
    const type = component?.types?.[0];
    if (!pincode && type === "postal_code") pincode = component.long_name;
    if (!city && type === "locality") city = component.long_name;
  });
  if (!parts.length) return null;
  return {
    primary: parts[0] || "",
    // Pin / locality title: never a bare number or house code ("33" -> "Phase 1, Sector 44",
    // "F - 65" -> "F - 65, Arya Samaj Rd").
    label: getGeocodeLabel(parts),
    secondary: parts.slice(1).join(", "),
    full: parts.join(", "),
    pincode,
    city,
    latitude,
    longitude,
    // Raw Google result (address components / types) for city-state, pincode and accuracy in the map form.
    result: preferred,
  };
};

/**
 * Reverse geocodes a coordinate with the Geocoding web service in a single request (same API as the app).
 * @param {number} latitude
 * @param {number} longitude
 * @returns {Promise<Object|null>} parsed address (see parseGeocodeResults), or null on failure.
 */
export async function reverseGeocodeLatLng(latitude, longitude, options = {}) {
  try {
    const url = `${GOOGLE_GEOCODE_API_URL}?latlng=${latitude},${longitude}&key=${GOOGLE_GEOCODE_API_KEY}`;
    const response = await fetch(url);
    const data = await response.json();
    return data?.status === "OK" ? parseGeocodeResults(data.results, latitude, longitude, options) : null;
  } catch {
    return null;
  }
}

/**
 * Place details for a Google place id from the serviceability API: lat/long and pincode in one call
 * (replaces Google PlaceService.GetPlaceDetails + control/getPinCodeByPostalAddress).
 * @param {string} placeId - Google place id.
 * @returns {Promise<Object|null>} { pincode, city, state, lat, long, ... } or null on failure.
 */
export async function getPlaceDetailsByPlaceId(placeId) {
  if (!placeId) return null;
  try {
    const response = await InterceptorClient.getGatewayInstance().get(fetchPlaceDetails(placeId), false);
    // The HTTP client resolves failures as { status, data }; only a successful body is used.
    if (response?.status) return null;
    const data = response?.data && typeof response.data === "object" ? response.data : response;
    return data && typeof data === "object" && ("pincode" in data || "lat" in data) ? data : null;
  } catch {
    return null;
  }
}
